<template>
  <div>
    <v-data-table
      dense
      disable-pagination
      disable-sort
      hide-default-footer
      :items="currentArtwork.dimensions"
      :headers="headers">
      <template v-slot:[`item.type`]="{ item }">
        {{ getCaptionText(item) }}
      </template>
      <template v-slot:[`item.size`]="{ item }">
        {{ getItemsText(item) }}
      </template>
      <template v-slot:[`item.edit`]="{ item }">
        <v-icon
        small
        class="mr-1"
        @click="editItem(item)">
        mdi-pencil-outline
        </v-icon>
        <v-icon
        small
        @click="deleteItem(item)">
        mdi-delete-outline
        </v-icon>
      </template>
      <template v-slot:no-data>
        {{ $t('artwork.dimension_no_data') }}
      </template>
    </v-data-table>
    <v-col class="text-right">
      <v-btn fab small color="primary" @click="addItem()">
        <v-icon>mdi-plus</v-icon>
      </v-btn>
    </v-col>
    <v-dialog persistent v-model="this.showDimensionsEditor" max-width="500">
      <v-card class="px-2 py-2">
        <v-card-title>
          {{ $t('fields.dimensions') }}
        </v-card-title>
        <v-container>
          <v-form ref="form">
            <v-select
              v-model="selectedDimensionId"
              :label="$t('artwork.dimension_type')"
              :items="this.dimensionList"
              item-value="id"
              item-text="name"
              outlined>
            </v-select>
            <v-text-field
              v-model="currentDimension.description"
              :label="$t('artwork.dimension_description')"
              outlined
              :rules="descriptionRules">
            </v-text-field>
            <div v-for="item in currentDimension.items" :key="item.dimensionItemId">
              <v-text-field
                :label="selectedDimensionItems.find(i => i.id == item.dimensionItemId).name"
                :rules="itemsRules.get(item.dimensionUnitId)"
                :suffix="selectedDimensionItems.find(i => i.id == item.dimensionItemId).dimensionName"
                outlined
                v-model="item.value">
              </v-text-field>
            </div>
          </v-form>
        </v-container>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text
            @click="cancelEdit()">{{$t('common_actions.cancel')}}</v-btn>
          <v-btn outlined color="primary" @click="saveItem()">{{$t('common_actions.save')}}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <confirm-dialog :show-dialog="this.showConfirmDeleteDialog"
      confirm-color="error"
      :question-text="$t('common_actions.confirm_delete')"
      :positive-text="$t('common_actions.delete')"
      :negative-text="$t('common_actions.cancel')"
      @onCancel="cancelDeleteItem"
      @onConfirm="confirmDeleteItem">
    </confirm-dialog>
  </div>
</template>

<script>
import i18n from '@/plugins/i18n'
import ConfirmDialog from '@/components/common/ConfirmDialog'

export default {
  components: { ConfirmDialog },
  data () {
    return {
      showConfirmDeleteDialog: false,
      showDimensionsEditor: false,
      selectedDimensionId: -1,
      selectedDimensionItems: [],
      currentDimension: {
        id: 0,
        description: '',
        items: []
      },
      editedDimension: null,
      itemsRules: new Map(),
      descriptionRules: [
        v => !!v || i18n.t('common_field_error.field_required')
      ],
      headers: [
        {
          text: i18n.t('artwork.dimension_description'),
          value: 'description',
          align: 'start',
          sortable: false
        },
        {
          text: i18n.t('artwork.dimension_type'),
          value: 'type',
          align: 'start',
          sortable: false
        },
        {
          text: i18n.t('artwork.dimension_size'),
          value: 'size',
          align: 'start',
          sortable: false
        },
        {
          text: i18n.t('artwork.dimension_edit'),
          value: 'edit',
          align: 'start',
          sortable: false
        }
      ]
    }
  },
  computed: {
    dimensionList () {
      return this.$store.getters['dimensions/dimensionsList']
    },
    currentArtwork () {
      return this.$store.getters['artworks/currentArtwork']
    }
  },
  mounted () {
    if (this.dimensionList.length > 0) {
      this.selectedDimensionId = this.dimensionList[0].id
    }

    this.dimensionList.forEach(d => {
      d.items.forEach(di => {
        if (!this.itemsRules.has(di.unit.id)) {
          this.itemsRules.set(di.unit.id, [
            v => !!v || i18n.t('common_field_error.field_required'),
            v => new RegExp(di.unit.pattern).test(v) || i18n.t('common_field_error.field_wrong_format')
          ])
        }
      })
    })
  },
  methods: {
    getCaptionText (dimension) {
      var type = this.dimensionList.find(dt => dt.id === dimension.dimensionTypeId)
      var info = ''

      type.items.forEach(di => {
        if (info.length > 0) {
          info = info + ' x '
        }

        info = info + di.name
      })

      return type.name + ' (' + info + ')'
    },
    getItemsText (dimension) {
      var type = this.dimensionList.find(dt => dt.id === dimension.dimensionTypeId)
      var result = ''

      dimension.items.forEach(item => {
        if (result.length > 0) {
          result = result + ' x '
        }

        var unit = type.items.find(di => di.id === item.dimensionItemId).unit.name

        result = result + item.value + ' ' + unit
      })

      return result
    },
    addItem () {
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }

      this.currentDimension = {
        id: 0,
        description: '',
        items: []
      }

      this.selectedDimensionItems.forEach(i => this.currentDimension.items.push({
        dimensionItemId: i.id,
        dimensionUnitId: i.dimensionUnitId,
        value: ''
      }))

      this.editedDimension = null
      this.showDimensionsEditor = true
    },
    editItem (item) {
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }

      this.currentDimension = {
        id: item.id,
        description: item.description,
        items: []
      }

      item.items.forEach(i => this.currentDimension.items.push({
        dimensionItemId: i.dimensionItemId,
        dimensionUnitId: i.dimensionUnitId,
        value: i.value
      }))

      this.editedDimension = item
      this.selectedDimensionId = item.dimensionTypeId
      this.showDimensionsEditor = true
    },
    deleteItem (item) {
      this.showConfirmDeleteDialog = true
      this.editedDimension = item
    },
    cancelDeleteItem () {
      this.showConfirmDeleteDialog = false
      this.editedDimension = null
    },
    confirmDeleteItem () {
      this.showConfirmDeleteDialog = false
      this.currentArtwork.dimensions.splice(this.currentArtwork.dimensions.indexOf(this.editedDimension), 1)
      this.editedDimension = null
      this.$emit('onChange')
    },
    cancelEdit () {
      this.showDimensionsEditor = false
    },
    saveItem () {
      if (!this.$refs.form.validate()) {
        return
      }

      let item = {
        id: 0,
        items: []
      }

      if (this.editedDimension != null) {
        item = this.currentArtwork.dimensions.find(d => d.id === this.editedDimension.id)
      }

      item.description = this.currentDimension.description

      let items = []

      this.currentDimension.items.forEach(i => {
        items.push({
          dimensionItemId: i.dimensionItemId,
          dimensionUnitId: i.dimensionUnitId,
          value: i.value
        })
      })

      item.dimensionTypeId = this.selectedDimensionId
      item.items = items

      if (this.editedDimension == null) {
        this.currentArtwork.dimensions.push(item)
      }

      this.showDimensionsEditor = false
      this.$emit('onChange')
    },
    reloadSelectedDimensionItems (dimensionTypeId) {
      let dimensionType = this.dimensionList.find(d => d.id === dimensionTypeId)
      let dimensionItems = []

      dimensionType.items.forEach(item => {
        dimensionItems.push({
          name: item.name,
          id: item.id,
          dimensionUnitId: item.unit.id,
          dimensionName: item.unit.name
        })
      })

      this.selectedDimensionItems = dimensionItems

      if (this.showDimensionsEditor) {
        this.currentDimension.items = []

        this.selectedDimensionItems.forEach(i => this.currentDimension.items.push({
          dimensionItemId: i.id,
          dimensionUnitId: i.dimensionUnitId,
          value: ''
        }))
      }
    }
  },
  watch: {
    selectedDimensionId (newValue) {
      this.reloadSelectedDimensionItems(newValue)
    }
  }
}

</script>



// WEBPACK FOOTER //
// src/components/dimensions/DimensionsEditor.vue